import React, { useEffect, useRef, useState } from "react";
import { currentUrl } from "../../App";
import { useLocation } from "react-router-dom";
import { Button, Col, Container, InputGroup, Row, Spinner } from "react-bootstrap";
import { fetchCheckInOut, fetchUserCasesFiltered, fetchUserCasesImages } from "../../services/httpClient";
import CheckInCard from "./CheckInCard";
import SearchBar from "../ui/SearchBar";
import { signal } from "@preact/signals-react";

interface CheckState {
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

export const resetCards = signal("all");
export const searchTxt = signal("");

const sortByLastName = (a: any, b: any) => {
  const getFirstName = (name: string) => name?.split(" ")[0].toLowerCase() || "";
  const getLastName = (name: string) => name?.split(" ").pop()?.toLowerCase() || "";
  if (getLastName(a.name) === getLastName(b.name)) {
    return getFirstName(a.name).localeCompare(getFirstName(b.name));
  }
  return getLastName(a.name).localeCompare(getLastName(b.name));
};

const CheckIn = ({ setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: CheckState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const [allCases, setAllCases] = useState<any[]>([]);
  const [allPeople, setAllPeople] = useState<any[]>([]);
  const [filteredAllPeople, setFilteredAllPeople] = useState<any[]>([]);
  const [allCheckData, setAllCheckData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [viewIn, setViewIn] = useState<boolean>(false);
  const [viewOut, setViewOut] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isImagesLoading, setIsImagesLoading] = useState<boolean>(true);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      setViewIn(false);
      setViewOut(false);
      setViewAll(true);
      getActivePeople(true);
    }
  }, [getData]);

  useEffect(() => {
    searchTxt.value = searchText;
  }, [searchText]);

  const getActivePeople = async (reload: boolean) => {
    if (reload) {
      setIsLoading(true);
    }

    //get without images first
    fetchUserCasesFiltered(false, "active")
      .then(async (res: any) => {
        var activecases = res[0].data;
        setAllCases(activecases);

        var allPpl = activecases
          .flatMap((caseObj: any) => [
            ...caseObj.family.map((f: any) => ({
              ...f,
              name: f.user_name,
              id: f.user_id,
              type: "guardian",
            })),
            ...caseObj.members.map((m: any) => ({
              ...m,
              name: m.member_name,
              type: "member",
            })),
          ])
          .filter((p: any) => p.is_banned !== 1)
          .sort(sortByLastName);

        setAllPeople(allPpl);
        setFilteredAllPeople(searchText ? searchLogicByPeople(searchText.toLowerCase(), allPpl) : allPpl);

        const checkData = await getCheckInOut(activecases);
        loadImages(allPpl, checkData);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCheckInOut = (activeCases: any[] = allCases): Promise<any[]> => {
    return new Promise((resolve) => {
      fetchCheckInOut()
        .then((res: any) => {
          var checks = res[0].data;

          //convert time to local
          checks = checks.map((a: any) => {
            if (a?.check_date) {
              const utcDate = new Date(a?.check_date + " UTC");
              const localTime = utcDate.toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });

              return { ...a, check_date: localTime };
            } else {
              return a;
            }
          });

          setAllCheckData(checks);
          setIsLoading(false);
          resolve(checks);
        })
        .catch((err: any) => {
          console.log(err);
          setIsLoading(false);
        });
    });
  };

  // load and assign profile images
  function loadImages(allPpl: any[], checkData: any[]) {
    const familyIDs = allPpl.flatMap((p) => (p.user_id != null ? [p.user_id] : []));
    const memberIDs = allPpl.flatMap((p) => (p.id != null ? [p.id] : []));

    fetchUserCasesImages(familyIDs, memberIDs).then((res: any) => {
      const familyImages = res[0].data.data_fam || [];
      const memberImages = res[0].data.data_memb || [];
      const images = [...familyImages, ...memberImages];

      // Make a lookup for faster assignment
      const byMemberId = new Map<number, any>();
      const byUserId = new Map<number, any>();

      for (const p of allPpl) {
        if (p.id != null) byMemberId.set(p.id, p);
        if (p.user_id != null) byUserId.set(p.user_id, p);
      }

      for (const img of images) {
        let target: any | undefined;

        if (img.member_id != null) {
          target = byMemberId.get(img.member_id);
        } else if (img.user_id != null) {
          target = byUserId.get(img.user_id);
        }

        if (target) {
          if (!target.profile) target.profile = [];
          target.profile.push({ id: img.id, data: img.data });
        }
      }

      if (resetCards.value === "in") {
        const checkedInPpl = checkData.filter((p: any) => p.is_checked_out === 0);
        const filteredPeople = allPpl.filter((person: any) =>
          checkedInPpl.some((checked: any) => checked.person_id === person.id && checked.person_type === person.type)
        );

        if (searchTxt.value) {
          const lowerText = searchTxt.value.toLowerCase();
          setFilteredAllPeople(searchLogicByPeople(lowerText, filteredPeople));
        } else {
          setFilteredAllPeople([...filteredPeople]);
        }
      } else if (resetCards.value === "out") {
        const checkedInPpl = checkData.filter((p: any) => p.is_checked_out === 0);
        const filteredPeople = allPpl.filter(
          (person: any) => !checkedInPpl.some((checked: any) => checked.person_id === person.id && checked.person_type === person.type)
        );

        if (searchTxt.value) {
          const lowerText = searchTxt.value.toLowerCase();
          setFilteredAllPeople(searchLogicByPeople(lowerText, filteredPeople));
        } else {
          setFilteredAllPeople([...filteredPeople]);
        }
      } else {
        if (searchTxt.value) {
          const lowerText = searchTxt.value.toLowerCase();
          setFilteredAllPeople(searchLogicByPeople(lowerText, allPpl));
        } else {
          setFilteredAllPeople([...allPpl]);
        }
      }

      setIsImagesLoading(false);
    });
  }

  const onPerformSearchLogicByPerson = (text: string) => {
    setViewIn(false);
    setViewOut(false);
    setViewAll(true);
    const lowerText = text.toLowerCase();

    setFilteredAllPeople(searchLogicByPeople(lowerText, allPeople));
    setAllCheckData([...allCheckData]);
  };

  const searchLogicByPeople = (text: string, searchObject: any) => {
    return searchObject.filter((item: any) => item.name.toLowerCase().includes(text)).sort(sortByLastName);
  };

  const onClickCheckedIn = () => {
    resetCards.value = "in";
    setViewIn(true);
    setViewOut(false);
    setViewAll(false);
    setSearchText("");

    const checkedInPpl = allCheckData.filter((p: any) => p.is_checked_out === 0);
    const filteredPeople = allPeople.filter((person: any) =>
      checkedInPpl.some((checked: any) => checked.person_id === person.id && checked.person_type === person.type)
    );
    setFilteredAllPeople([...filteredPeople]);
    setAllCheckData([...allCheckData]);
  };

  const onClickCheckedOut = () => {
    resetCards.value = "out";
    setViewIn(false);
    setViewOut(true);
    setViewAll(false);
    setSearchText("");

    const checkedInPpl = allCheckData.filter((p: any) => p.is_checked_out === 0);
    const filteredPeople = allPeople.filter(
      (person: any) => !checkedInPpl.some((checked: any) => checked.person_id === person.id && checked.person_type === person.type)
    );
    setFilteredAllPeople([...filteredPeople]);
    setAllCheckData([...allCheckData]);
  };

  const onClickViewAll = () => {
    resetCards.value = "all";
    setViewIn(false);
    setViewOut(false);
    setViewAll(true);
    setSearchText("");

    setFilteredAllPeople([...allPeople]);
    setAllCheckData([...allCheckData]);
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <>
            <Row className="mb-0">
              <Col className="col-12">
                <h4 className="app-header-text">Check In / Check Out</h4>
              </Col>
            </Row>
            <Row className="mt-3 mb-4">
              <Col xs={12}>
                <InputGroup>
                  <Col>
                    <SearchBar
                      cssClass="search"
                      placeholder="Search Person Name"
                      searchText={searchText}
                      setSearchText={setSearchText}
                      searchLogic={onPerformSearchLogicByPerson}
                      externalRef={searchRef}
                    />
                  </Col>
                  <small className="medium-text app-header-text me-2 align-self-center">View By</small>
                  <Button
                    className={
                      viewIn
                        ? "app-primary-color me-2 button button-icon-filter rounded"
                        : "app-primary-color-inverse me-2 button button-icon-filter rounded"
                    }
                    onClick={() => onClickCheckedIn()}
                    disabled={isImagesLoading ? true : false}
                  >
                    In
                  </Button>
                  <Button
                    className={
                      viewOut
                        ? "app-primary-color me-2 button button-icon-filter rounded align-items-center justify-content-center d-flex"
                        : "app-primary-color-inverse me-2 button button-icon-filter rounded align-items-center justify-content-center d-flex"
                    }
                    onClick={() => onClickCheckedOut()}
                    disabled={isImagesLoading ? true : false}
                  >
                    Out
                  </Button>
                  <Button
                    className={
                      viewAll
                        ? "app-primary-color me-2 button button-icon-filter rounded"
                        : "app-primary-color-inverse me-2 button button-icon-filter rounded"
                    }
                    onClick={() => onClickViewAll()}
                  >
                    All
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            <Row className="justify-content-center mb-5">
              <Container className="card-container">
                {filteredAllPeople.length === 0 ? (
                  <p className="mt-2">No Active Members</p>
                ) : (
                  filteredAllPeople?.map((item: any, index: number) => (
                    <CheckInCard
                      key={item?.person_id}
                      person={item}
                      setFilteredAllPeople={setFilteredAllPeople}
                      allCheckData={allCheckData}
                      setAllCheckData={setAllCheckData}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))
                )}
              </Container>
            </Row>
          </>
        </>
      )}
    </>
  );
};

export default CheckIn;
